'use client';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { ButtonHTMLAttributes, ReactNode } from 'react';
import { VariantProps } from 'class-variance-authority';
import Image from 'next/image';

const iconButtonClasses = cva(
  'inline-flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-primary-background text-white hover:bg-primary-dark focus:ring-primary-light',
        secondary: 'bg-secondary-light text-text-primary hover:bg-secondary-dark focus:ring-secondary-background',
        outline: 'border-2 border-primary-background text-primary-background bg-transparent hover:bg-primary-light focus:ring-primary-light',
        ghost: 'bg-transparent hover:bg-secondary-light focus:ring-secondary-background',
      },
      size: {
        small: 'w-8 h-8',
        medium: 'w-10 h-10',
        large: 'w-12 h-12',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
)

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof iconButtonClasses> {
  // Required parameters with defaults
  src?: string;
  alt?: string;
  iconWidth?: number;
  iconHeight?: number;
  borderStyleCss?: string;
  padding?: string;
  
  // Optional parameters
  fillStyleCss?: string;
  layout_width?: string;
  position?: string;
  margin?: string;
  
  // Standard React props
  children?: ReactNode;
}

const IconButton = ({
  // Required parameters with defaults
  src = "/images/default-icon.svg",
  alt = "Icon",
  iconWidth = 24,
  iconHeight = 24,
  borderStyleCss = "",
  padding = "",
  
  // Optional parameters (no defaults)
  fillStyleCss,
  layout_width,
  position,
  margin,
  
  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = 'button',
  ...props
}: IconButtonProps) => {
  // Parse border styles
  const parseBorderStyles = (borderCss: string) => {
    const styles: string[] = []
    if (borderCss.includes('border=')) {
      const borderMatch = borderCss.match(/border=([^,]+)/)
      if (borderMatch) {
        const borderValue = borderMatch[1].trim()
        if (borderValue.includes('solid')) {
          const colorMatch = borderValue.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/)
          if (colorMatch) {
            styles.push(`border border-[${colorMatch[0]}]`)
          } else {
            styles.push('border')
          }
        }
      }
    }
    if (borderCss.includes('border_radius=')) {
      const radiusMatch = borderCss.match(/border_radius=([^,]+)/)
      if (radiusMatch) {
        const radiusValue = radiusMatch[1].trim().replace('px', '')
        styles.push(`rounded-[${radiusValue}px]`)
      }
    }
    return styles.join(' ')
  }

  // Parse padding
  const parsePadding = (paddingStr: string) => {
    if (!paddingStr) return ''
    const paddingParts = paddingStr.split(',')
    const paddingClasses: string[] = []
    
    paddingParts.forEach(part => {
      const [side, value] = part.split('=')
      const cleanValue = value?.replace('px', '')
      if (side && cleanValue) {
        switch (side.trim()) {
          case 't':
            paddingClasses.push(`pt-[${cleanValue}px]`)
            break
          case 'r':
            paddingClasses.push(`pr-[${cleanValue}px]`)
            break
          case 'b':
            paddingClasses.push(`pb-[${cleanValue}px]`)
            break
          case 'l':
            paddingClasses.push(`pl-[${cleanValue}px]`)
            break
        }
      }
    })
    
    return paddingClasses.join(' ')
  }

  // Parse background color
  const parseBackgroundColor = (fillCss: string) => {
    if (!fillCss) return ''
    if (fillCss.includes('background-color=')) {
      const colorMatch = fillCss.match(/background-color=([^,]+)/)
      if (colorMatch) {
        const color = colorMatch[1].trim()
        return `bg-[${color}]`
      }
    }
    return ''
  }

  // Safe validation for optional parameters
  const hasValidWidth = layout_width && typeof layout_width === 'string' && layout_width.trim() !== ''
  const hasValidMargin = margin && typeof margin === 'string' && margin.trim() !== ''
  const hasValidPosition = position && typeof position === 'string' && position.trim() !== ''

  const optionalClasses = [
    hasValidWidth ? `w-[${layout_width}]` : '',
    hasValidMargin ? `m-[${margin}]` : '',
    hasValidPosition ? position : '',
    parseBorderStyles(borderStyleCss),
    parsePadding(padding),
    parseBackgroundColor(fillStyleCss || ''),
  ].filter(Boolean).join(' ')

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault()
      return
    }
    
    if (typeof onClick === 'function') {
      onClick(event)
    }
  }

  return (
    <button data-component-id="src/components/ui/IconButton.tsx:171:4" data-component-path="src/components/ui/IconButton.tsx" data-component-line="171" data-component-end-line="192" data-component-file="IconButton.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      disabled={disabled}
      onClick={handleClick}
      className={twMerge(
        iconButtonClasses({ variant, size }),
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children || (
        <Image data-component-id="src/components/ui/IconButton.tsx:184:8" data-component-path="src/components/ui/IconButton.tsx" data-component-line="184" data-component-end-line="190" data-component-file="IconButton.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%5Bvar%3Asrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22className%22%3A%22object-contain%22%7D"
          src={src}
          alt={alt}
          width={iconWidth}
          height={iconHeight}
          className="object-contain"
        />
      )}
    </button>
  )
}

export default IconButton